import { Box, Chip, Paper, Stack, Typography, alpha, lighten } from "@mui/material";

/*
  UploadTile
  ──────────────────────────────────────────────────────────────
  One of the four source-type cards on the hub. Mirrors the
  mobile MicrolabCopilotPanel's UploadTile component but in MUI
  primitives. Hover state mirrors the existing AIQuizWizard look
  (translateY + soft coloured shadow).

  Props:
    icon       — MUI <Icon /> or material-icons component (already mounted)
    label      — primary label, e.g. "PDF"
    sublabel   — short hint under the label, e.g. "Slides, lecture notes"
    costLabel  — credit-cost pill, e.g. "5 credits"
    tint       — accent color hex, used for icon background + halo
    disabled   — boolean
    onClick    — handler
*/

export default function UploadTile({
  icon,
  label,
  sublabel,
  costLabel,
  tint = "#3B82F6",
  disabled,
  onClick,
}) {
  return (
    <Paper
      variant="outlined"
      onClick={disabled ? undefined : onClick}
      role={disabled ? undefined : "button"}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      sx={(theme) => ({
        p: { xs: 1.5, sm: 2 },
        height: "100%",
        minWidth: 0,
        width: "100%",
        boxSizing: "border-box",
        borderRadius: 3,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
        borderColor: alpha(tint, 0.25),
        backgroundColor:
          theme.palette.mode === "dark"
            ? alpha(tint, 0.08)
            : lighten(tint, 0.96),
        "&:hover": disabled
          ? undefined
          : {
              transform: "translateY(-2px)",
              boxShadow: `0 14px 28px ${alpha(tint, 0.18)}`,
              borderColor: alpha(tint, 0.55),
            },
        "&:focus-visible": {
          outline: `2px solid ${alpha(tint, 0.7)}`,
          outlineOffset: 2,
        },
      })}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: { xs: 40, sm: 44 },
            height: { xs: 40, sm: 44 },
            flexShrink: 0,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: { xs: 20, sm: 22 },
            backgroundImage: `linear-gradient(135deg, ${tint}, ${lighten(tint, 0.25)})`,
            boxShadow: `0 6px 14px ${alpha(tint, 0.32)}`,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            fontWeight={700}
            sx={{
              lineHeight: 1.15,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {sublabel}
          </Typography>
        </Box>
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mt={1.5}
        spacing={1}
        sx={{ minWidth: 0 }}
      >
        <Chip
          size="small"
          label={costLabel}
          sx={(theme) => ({
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: 0.3,
            maxWidth: "100%",
            color: theme.palette.mode === "dark" ? tint : theme.palette.getContrastText(lighten(tint, 0.7)),
            backgroundColor: alpha(tint, theme.palette.mode === "dark" ? 0.18 : 0.16),
          })}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            flexShrink: 0,
            display: { xs: "none", sm: "block" },
          }}
        >
          Click to upload
        </Typography>
      </Stack>
    </Paper>
  );
}
